<template>
  <SoDialog
    v-model="value"
    :loading="isPending"
    card-title="Confirm Maintenance Banner Changes"
    data-testid="maintenance-modal"
  >
    <template v-if="!isSuccess">
      <SoAlert
        type="warning"
        title="The following maintenance banner settings will be updated:"
        class="mb-4"
      />
      <div v-if="configChanges.bannerEnabled.changed" class="mb-2">
        <h6 class="font-weight-medium d-inline-block">Banner Enabled:</h6>
        <span class="ml-2">
          {{ configChanges.bannerEnabled.from ? "Yes" : "No" }}
        </span>
      </div>
      <div v-if="configChanges.title.changed" class="mb-2">
        <h6 class="font-weight-medium">Title:</h6>
        <p>{{ configChanges.title.from }}</p>
      </div>
      <div v-if="configChanges.message.changed" class="mb-2">
        <h6 class="font-weight-medium">Message:</h6>
        <div v-html="configChanges.message.from"></div>
      </div>
      <SoSimpleTable
        v-if="configChanges.pages.length"
        :headers="['Page', 'Banner Enabled']"
        :items="
          configChanges.pages.map((page) => [
            page.name,
            page.enabled ? 'Yes' : 'No',
          ])
        "
        class="mt-4 border rounded"
        data-testid="maintenance-modal-pages-table"
      />
    </template>
    <SoAlert
      v-if="isSuccess"
      title="Changes saved"
      type="success"
      class="mt-4"
      data-testid="maintenance-modal-success-alert"
    />
    <SoAlert
      v-if="isError"
      title="Failed to save changes"
      type="error"
      class="mt-4"
      data-testid="maintenance-modal-error-alert"
    />
    <div class="d-flex justify-end mt-8">
      <template v-if="isSuccess">
        <SoButton
          title="Close"
          large
          data-testid="maintenance-modal-close-btn"
          @click="value = false"
        />
      </template>
      <template v-else>
        <SoButton
          title="Cancel"
          class="mr-2"
          large
          data-testid="maintenance-modal-cancel-btn"
          @click="value = false"
        />
        <SoButton
          title="Save"
          type="success"
          large
          data-testid="maintenance-modal-save-btn"
          :loading="isPending"
          @click="saveChanges"
        />
      </template>
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"

  interface MaintenancePage {
    id: number
    name: string
    enabled: boolean
  }

  export default defineComponent({
    components: {
      SoAlert,
      SoButton,
      SoDialog,
      SoSimpleTable,
    },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
        required: true,
      },
      isPending: {
        type: Boolean,
        required: true,
      },
      isSuccess: {
        type: Boolean,
        required: true,
      },
      isError: {
        type: Boolean,
        required: true,
      },
      configChanges: {
        type: Object as PropType<{
          bannerEnabled: { changed: boolean; from: boolean; to: boolean }
          title: { changed: boolean; from: string; to: string }
          message: { changed: boolean; from: string; to: string }
          pages: MaintenancePage[]
        }>,
        required: true,
      },
    },
    emits: ["update:modelValue", "save"],
    computed: {
      value: {
        get() {
          return this.modelValue
        },
        set(value: boolean) {
          this.$emit("update:modelValue", value)
        },
      },
    },
    methods: {
      async saveChanges() {
        this.$emit("save")
      },
    },
  })
</script>
